"use client";

import Link from "next/link";
import { useAuth } from "@/hooks/useAuth";
import { DEMO_CTA_URL } from "@/lib/cta";

interface MarketingTopbarProps {
  variant?: "default" | "auth-login" | "auth-signup";
}

// Wordmark text node stays the literal lowercase "thexis" (so the AppShell
// route-classification test `getByText("thexis")` keeps passing); CSS
// uppercase + letterspacing renders it as THEXIS, Legora-style.
const wordmark =
  "font-serif text-[22px] font-medium uppercase leading-none tracking-[0.22em] text-foreground sm:text-[24px]";

const navLink =
  "inline-flex min-h-[44px] items-center px-2 text-[14px] text-muted-foreground transition-colors hover:text-foreground";

const ctaPill =
  "inline-flex min-h-[44px] items-center rounded-full bg-accent px-5 text-[14px] font-medium text-accent-foreground transition-colors hover:bg-accent-strong";

export function MarketingTopbar({ variant = "default" }: MarketingTopbarProps) {
  const { user, isLoading } = useAuth();
  const showDashboardLink = Boolean(user) && !isLoading;

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-background/85 backdrop-blur-md">
      <div className="mx-auto w-full max-w-[1280px] px-6 py-4 sm:px-10">
        {variant === "default" ? (
          <div className="grid grid-cols-[auto_1fr] items-center gap-4 sm:grid-cols-[1fr_auto_1fr]">
            {/* left: section links */}
            <nav className="hidden items-center gap-7 sm:flex">
              <Link href="#how-it-works" className={navLink}>
                How it works
              </Link>
              <Link href="#for-firms" className={navLink}>
                For firms
              </Link>
            </nav>
            {/* centre: wordmark */}
            <Link
              href="/"
              aria-label="Thexis — home"
              className={`${wordmark} justify-self-start sm:justify-self-center`}
            >
              thexis
            </Link>
            {/* right: actions */}
            <div className="flex items-center justify-end gap-3 sm:gap-5">
              {showDashboardLink ? (
                <a href="/dashboard" className={ctaPill}>
                  Open dashboard →
                </a>
              ) : (
                <>
                  {/* Base `hidden` (NOT navLink's base inline-flex, which would
                      override hidden via Tailwind source order); shown sm+. */}
                  <Link
                    href="/login"
                    className="hidden min-h-[44px] items-center px-2 text-[14px] text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
                  >
                    Sign in
                  </Link>
                  <a href={DEMO_CTA_URL} className={ctaPill}>
                    Request access
                  </a>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <Link href="/" aria-label="Thexis — home" className={wordmark}>
              thexis
            </Link>
            <nav className="-my-2 flex items-center text-sm font-medium">
              {variant === "auth-login" && (
                <Link href="/signup" className={navLink}>
                  <span className="hidden sm:inline">New here?&nbsp;</span>
                  Request access
                </Link>
              )}
              {variant === "auth-signup" && (
                <Link href="/login" className={navLink}>
                  <span className="hidden sm:inline">
                    Already have an account?&nbsp;
                  </span>
                  Sign in
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
